import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AlertService } from 'src/app/shared/services/alert.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ITopicWithTranslations } from 'src/app/core/models/i-topic-with-translations';
import { IDeliveryTarget } from 'src/app/core/models/i-delivery-target';
import { ISaveTopicRequest } from 'src/app/core/models/i-save-topic-request';
import { ILanguage } from 'src/app/core/models/i-language';
import { TopicService } from 'src/app/content-management/shared/services/topic.service';
import { DeliveryTargetsService } from 'src/app/content-list-management/shared/services/cmsapi/delivery-targets.service';
import { LanguageService } from 'src/app/content-management/shared/services/language.service';
import { toIsoLanguage } from './language-code';

@Component({
  selector: 'app-edit-topic',
  templateUrl: './edit-topic.component.html',
  styles: [
    `
      .topic-tabs ::ng-deep .mat-tab-label {
        min-width: 0;
        padding: 0 16px;
        opacity: 1;
      }
    `,
  ],
})
export class EditTopicComponent implements OnInit, OnDestroy {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private topicService: TopicService,
    private deliveryTargetsService: DeliveryTargetsService,
    private languageService: LanguageService,
    private alertService: AlertService,
    private formBuilder: FormBuilder,
    public dialogService: DialogService,
  ) {}

  subscriptions: Subscription[] = [];
  topicId: number | null = null;
  isNew = true;
  isSaving = false;
  allTopics: ITopicWithTranslations[] = [];
  parentOptions: ITopicWithTranslations[] = [];
  deliveryTargets: IDeliveryTarget[] = [];
  availableLanguages: { code: string; friendlyName: string }[] = [];
  form: UntypedFormGroup;
  private initialSnapshot = '';
  private languageOptionsCache = new Map<
    string,
    { code: string; friendlyName: string }[]
  >();

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    this.isNew = !idParam || idParam === 'new';
    this.topicId = this.isNew ? null : Number(idParam);

    this.form = this.formBuilder.group({
      name: [
        '',
        [Validators.required, Validators.pattern(/\S/), Validators.maxLength(250)],
      ],
      parentId: [null],
      channels: [[]],
      translations: this.formBuilder.array([]),
    });
    this.initialSnapshot = this.buildSnapshot();

    this.loadDeliveryTargets();
    this.loadLanguages();
    this.loadTopics();
    if (!this.isNew && this.topicId !== null) {
      this.loadTopic();
    }
  }

  loadDeliveryTargets(): void {
    const deliveryTargetsSubscription = this.deliveryTargetsService
      .getAll$()
      .subscribe(
        (targets) => (this.deliveryTargets = targets),
        () =>
          this.dialogService.openErrorDialog({
            message:
              'An error has occurred whilst loading the delivery targets. Please contact IT.',
          }),
      );
    this.subscriptions.push(deliveryTargetsSubscription);
  }

  loadLanguages(): void {
    const languagesSubscription = this.languageService.getAll$().subscribe(
      (languages) => {
        this.availableLanguages = this.buildAvailableLanguages(languages ?? []);
        this.languageOptionsCache.clear();
      },
      () =>
        this.dialogService.openErrorDialog({
          message:
            'An error has occurred whilst loading the languages. Please contact IT.',
        }),
    );
    this.subscriptions.push(languagesSubscription);
  }

  buildAvailableLanguages(
    languages: ILanguage[],
  ): { code: string; friendlyName: string }[] {
    const byIso = new Map<string, string>();
    languages.forEach((language) => {
      const code = toIsoLanguage(language.Code);
      if (code && !byIso.has(code)) {
        byIso.set(code, language.FriendlyName ?? code.toUpperCase());
      }
    });
    return Array.from(byIso, ([code, friendlyName]) => ({ code, friendlyName }));
  }

  loadTopics(): void {
    const topicsSubscription = this.topicService.getFiltered$().subscribe(
      (topics) => {
        this.allTopics = topics ?? [];
        this.parentOptions = this.buildParentOptions();
      },
      () =>
        this.dialogService.openErrorDialog({
          message:
            'An error has occurred whilst loading the topics. Please contact IT.',
        }),
    );
    this.subscriptions.push(topicsSubscription);
  }

  loadTopic(): void {
    const topicSubscription = this.topicService
      .getById$(this.topicId)
      .subscribe(
        (topic) => this.populateForm(topic),
        () =>
          this.dialogService.openErrorDialog({
            message: 'The requested topic could not be found.',
          }),
      );
    this.subscriptions.push(topicSubscription);
  }

  populateForm(current: ITopicWithTranslations): void {
    if (!current) {
      this.dialogService.openErrorDialog({
        message: 'The requested topic could not be found.',
      });
      return;
    }
    this.form.patchValue({
      name: current.name,
      parentId: current.parentId ?? null,
      channels: current.channels ?? [],
    });

    this.translations.clear();
    (current.displayNames ?? []).forEach((translation) =>
      this.addTranslation(translation.language, translation.displayName),
    );

    this.initialSnapshot = this.buildSnapshot();
  }

  get translations(): FormArray {
    return this.form.get('translations') as FormArray;
  }

  // Compare the current form against its original values so reverting a change
  // re-disables the Save button (Angular's dirty flag does not reset on revert).
  buildSnapshot(): string {
    const value = this.form.getRawValue();
    const channels = [...(value.channels ?? [])].sort();
    const translations = ((value.translations ?? []) as {
      language: string;
      displayName: string;
    }[])
      .map((translation) => ({
        language: toIsoLanguage(translation.language),
        displayName: (translation.displayName ?? '').trim(),
      }))
      .sort((a, b) => a.language.localeCompare(b.language));
    return JSON.stringify({
      name: (value.name ?? '').trim(),
      parentId: value.parentId ?? null,
      channels,
      translations,
    });
  }

  hasChanges(): boolean {
    return this.buildSnapshot() !== this.initialSnapshot;
  }

  addTranslation(language = '', displayName = ''): void {
    this.translations.push(
      this.formBuilder.group({
        language: [toIsoLanguage(language), Validators.required],
        displayName: [
          displayName,
          [Validators.required, Validators.maxLength(250)],
        ],
      }),
    );
  }

  removeTranslation(index: number): void {
    this.translations.removeAt(index);
  }

  languageOptions(
    currentCode: string,
  ): { code: string; friendlyName: string }[] {
    const code = toIsoLanguage(currentCode);
    const key = code ?? '';
    const cached = this.languageOptionsCache.get(key);
    if (cached) {
      return cached;
    }
    const options =
      code && !this.availableLanguages.some((l) => l.code === code)
        ? [{ code, friendlyName: code.toUpperCase() }, ...this.availableLanguages]
        : this.availableLanguages;
    this.languageOptionsCache.set(key, options);
    return options;
  }

  trackByLanguageCode(_index: number, language: { code: string }): string {
    return language.code;
  }

  isLanguageTaken(code: string, currentIndex: number): boolean {
    const iso = toIsoLanguage(code);
    return this.translations.controls.some(
      (control, index) =>
        index !== currentIndex &&
        toIsoLanguage(control.get('language')?.value) === iso,
    );
  }

  buildParentOptions(): ITopicWithTranslations[] {
    const topLevelTopics = this.allTopics.filter(
      (topic) => topic.parentId == null,
    );
    if (this.isNew || this.topicId === null) {
      return topLevelTopics;
    }
    const currentId = this.topicId;
    const excluded = this.descendantIds(currentId);
    excluded.add(currentId);
    return topLevelTopics.filter((topic) => !excluded.has(topic.id));
  }

  descendantIds(rootId: number): Set<number> {
    const result = new Set<number>();
    const stack: number[] = [rootId];
    while (stack.length) {
      const current = stack.pop();
      this.allTopics
        .filter((topic) => topic.parentId === current)
        .forEach((child) => {
          if (!result.has(child.id)) {
            result.add(child.id);
            stack.push(child.id);
          }
        });
    }
    return result;
  }

  isChannelSelected(code: string): boolean {
    const channels: string[] = this.form.get('channels')?.value ?? [];
    return channels.includes(code);
  }

  toggleChannel(code: string, checked: boolean): void {
    const channels: string[] = this.form.get('channels')?.value ?? [];
    const updated = checked
      ? [...channels, code]
      : channels.filter((channel) => channel !== code);
    this.form.get('channels')?.setValue(updated);
  }

  save(): void {
    if (this.form.invalid) {
      this.dialogService.openErrorDialog({
        message: 'A valid topic name is required.',
      });
      return;
    }

    const value = this.form.getRawValue();
    const request: ISaveTopicRequest = {
      name: value.name.trim(),
      parentId: value.parentId ?? undefined,
      channels: value.channels ?? [],
      displayNames: ((value.translations ?? []) as {
        language: string;
        displayName: string;
      }[])
        .filter(
          (translation) =>
            translation.language && translation.displayName?.trim(),
        )
        .map((translation) => ({
          language: toIsoLanguage(translation.language),
          displayName: translation.displayName.trim(),
        })),
    };

    this.isSaving = true;
    const request$ =
      this.isNew || this.topicId === null
        ? this.topicService.create$(request)
        : this.topicService.update$(this.topicId, request);

    const saveSubscription = request$.subscribe(
      () => {
        this.isSaving = false;
        this.alertService.success(
          'Topic: ' +
            request.name +
            (this.isNew ? ' created successfully' : ' updated successfully'),
        );
        this.router.navigate(['/admin/topics']);
      },
      (error) => {
        this.isSaving = false;
        this.dialogService.openErrorDialog({
          message: this.getErrorMessage(
            error,
            'An error has occurred whilst saving the topic. Please contact IT.',
          ),
        });
      },
    );
    this.subscriptions.push(saveSubscription);
  }

  cancel(): void {
    this.router.navigate(['/admin/topics']);
  }

  getErrorMessage(error: { error?: unknown }, fallback: string): string {
    return typeof error?.error === 'string' && error.error
      ? error.error
      : fallback;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}
